/**
 * @file     AcademyContext.tsx
 * @readme   ./README.md
 * @purpose  Context provider for academy state. Persists progress to localStorage for anonymous users, Supabase for authenticated.
 */
'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import type { AcademyState, LessonProgress, UserBadge, UserStreak } from './types';
import { buildXpMap, canonicalProgress } from './configs';
import { calculateTotalXp } from './xp';

const LOCAL_PROGRESS_KEY = 'relta-academy-progress';
const LOCAL_PROGRESS_VERSION = 1;

interface AcademyContextValue extends AcademyState {
  markLessonComplete: (lessonId: string, score?: number) => Promise<void>;
  refreshProgress: () => Promise<void>;
}

const AcademyContext = createContext<AcademyContextValue | null>(null);

export function useAcademy(): AcademyContextValue {
  const ctx = useContext(AcademyContext);
  if (!ctx) throw new Error('useAcademy must be used within <AcademyProvider>');
  return ctx;
}

function readLocalProgress(): Record<string, LessonProgress> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(LOCAL_PROGRESS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed?.version !== LOCAL_PROGRESS_VERSION) return {};
    return canonicalProgress(parsed.data ?? {});
  } catch {
    return {};
  }
}

function writeLocalProgress(progress: Record<string, LessonProgress>) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(LOCAL_PROGRESS_KEY, JSON.stringify({
      version: LOCAL_PROGRESS_VERSION,
      data: progress,
      updatedAt: new Date().toISOString(),
    }));
  } catch {
    // Storage full or unavailable
  }
}

export function AcademyProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [progress, setProgress] = useState<Record<string, LessonProgress>>({});
  const [streak, setStreak] = useState<UserStreak | null>(null);
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const xpMap = useMemo(() => buildXpMap(), []);
  const totalXp = useMemo(() => calculateTotalXp(progress, xpMap), [progress, xpMap]);

  const refreshProgress = useCallback(async () => {
    // Always load local progress first (covers anonymous + initial load)
    const localProgress = readLocalProgress();
    if (Object.keys(localProgress).length > 0) {
      setProgress(localProgress);
    }

    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      const [progressRes, streakRes] = await Promise.all([
        fetch('/api/academy/resume'),
        fetch('/api/academy/streak'),
      ]);
      if (progressRes.ok) {
        const data = await progressRes.json();
        if (data.progress && Object.keys(data.progress).length > 0) {
          // Merge: server wins for conflicts, but keep local-only entries
          const merged = { ...localProgress, ...canonicalProgress<LessonProgress>(data.progress) };
          setProgress(merged);
          writeLocalProgress(merged);
        }
        if (data.badges) setBadges(data.badges);
      }
      if (streakRes.ok) {
        const data = await streakRes.json();
        if (data.streak) setStreak(data.streak);
      }
    } catch {
      // Silently fail, local progress is already loaded
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshProgress();
  }, [refreshProgress]);

  const markLessonComplete = useCallback(async (lessonId: string, score = 100) => {
    const newEntry: LessonProgress = {
      lessonId,
      status: 'completed',
      score,
      completedAt: new Date().toISOString(),
    };

    // Optimistic update + persist to localStorage
    setProgress((prev) => {
      const updated = { ...prev, [lessonId]: newEntry };
      writeLocalProgress(updated);
      return updated;
    });

    if (!isAuthenticated) return;

    try {
      const { secureFetch } = await import('@/services/anti-scraping/secureFetch');
      await secureFetch('/api/academy/progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lessonId, status: 'completed', score }),
      });
    } catch {
      // Local progress already saved, server sync will happen on next load
    }
  }, [isAuthenticated]);

  const value = useMemo<AcademyContextValue>(() => ({
    progress,
    streak,
    badges,
    totalXp,
    isLoading,
    markLessonComplete,
    refreshProgress,
  }), [progress, streak, badges, totalXp, isLoading, markLessonComplete, refreshProgress]);

  return (
    <AcademyContext.Provider value={value}>
      {children}
    </AcademyContext.Provider>
  );
}
